import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import { API_URL } from "@/shared"
import { BiocellsmedUser, CreateQualityRedFlagDto, CreateQualityRewardDto, QualityUserHistoryResponse, QualityUserRewardsHistoryResponse } from "../domain/quality-red-flag"

export const QUALITY_KEYS = {
  GET_USERS: "QUALITY_GET_USERS",
  GET_USER_SUMMARY: "QUALITY_GET_USER_SUMMARY",
  GET_HISTORY: "QUALITY_GET_HISTORY",
  GET_USER_HISTORY: "QUALITY_GET_USER_HISTORY",
  GET_USER_REWARDS_HISTORY: "QUALITY_GET_USER_REWARDS_HISTORY",
  CREATE: "QUALITY_CREATE",
  SEND_REWARD: "QUALITY_SEND_REWARD"
}

export const useGetBiocellsmedUsers = (params?: Record<string, string | number | undefined>) => {
  return useQuery({
    queryKey: [QUALITY_KEYS.GET_USERS, params],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/quality-red-flags/users`, { params })
      return data as { data: BiocellsmedUser[]; totalRecords: number }
    },
    initialData: { data: [], totalRecords: 0 }
  })
}

export const useGetBiocellsmedUserSummary = (userId: string) => {
  return useQuery({
    queryKey: [QUALITY_KEYS.GET_USER_SUMMARY, userId],
    queryFn: async () => {
      if (!userId) return null
      const { data } = await axios.get(`${API_URL}/quality-red-flags/users/${userId}`)
      return data.data as BiocellsmedUser
    },
    enabled: !!userId,
    staleTime: 0,
    gcTime: 0
  })
}

export const useGetQualityUserHistory = (userId: string, params?: Record<string, string | number | undefined>) => {
  return useQuery<QualityUserHistoryResponse, Error>({
    queryKey: [QUALITY_KEYS.GET_USER_HISTORY, userId, params],
    queryFn: async () => {
      if (!userId) return { success: true, data: [], total: 0, user: null } as QualityUserHistoryResponse
      const { data } = await axios.get<QualityUserHistoryResponse>(`${API_URL}/quality-red-flags/history/${userId}`, { params })
      return data
    },
    enabled: !!userId
  })
}

export const useGetQualityUserRewardsHistory = (userId: string, params?: Record<string, string | number | undefined>) => {
  return useQuery<QualityUserRewardsHistoryResponse, Error>({
    queryKey: [QUALITY_KEYS.GET_USER_REWARDS_HISTORY, userId, params],
    queryFn: async () => {
      if (!userId) return { success: true, data: [], total: 0, user: null } as QualityUserRewardsHistoryResponse
      const { data } = await axios.get<QualityUserRewardsHistoryResponse>(`${API_URL}/quality-red-flags/rewards/history/${userId}`, { params })
      return data
    },
    enabled: !!userId
  })
}

export const useCreateQualityRedFlag = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (dto: CreateQualityRedFlagDto) => {
      const { data } = await axios.post(`${API_URL}/quality-red-flags`, dto)
      return data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUALITY_KEYS.GET_HISTORY] })
      void queryClient.invalidateQueries({ queryKey: [QUALITY_KEYS.GET_USERS] })
      void queryClient.invalidateQueries({ queryKey: [QUALITY_KEYS.GET_USER_HISTORY] })
      void queryClient.invalidateQueries({ queryKey: [QUALITY_KEYS.GET_USER_SUMMARY] })
    }
  })
}

export const useSendQualityReward = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (dto: CreateQualityRewardDto) => {
      const { data } = await axios.post(`${API_URL}/quality-red-flags/rewards`, dto)
      return data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUALITY_KEYS.GET_USER_REWARDS_HISTORY] })
    }
  })
}

export const useDeleteLastQualityRedFlag = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await axios.delete(`${API_URL}/quality-red-flags/${userId}/last`)
      return data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUALITY_KEYS.GET_HISTORY] })
      void queryClient.invalidateQueries({ queryKey: [QUALITY_KEYS.GET_USERS] })
      void queryClient.invalidateQueries({ queryKey: [QUALITY_KEYS.GET_USER_HISTORY] })
      void queryClient.invalidateQueries({ queryKey: [QUALITY_KEYS.GET_USER_SUMMARY] })
    }
  })
}

export const useResetQualityRedFlags = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await axios.delete(`${API_URL}/quality-red-flags/${userId}/reset`)
      return data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUALITY_KEYS.GET_HISTORY] })
      void queryClient.invalidateQueries({ queryKey: [QUALITY_KEYS.GET_USERS] })
      void queryClient.invalidateQueries({ queryKey: [QUALITY_KEYS.GET_USER_HISTORY] })
      void queryClient.invalidateQueries({ queryKey: [QUALITY_KEYS.GET_USER_SUMMARY] })
    }
  })
}
